import React, {useEffect, useState} from 'react';
import {Check, ChevronDownIcon} from "lucide-react";

// CREATING A CUSTOM DROPDOWN COMPONENT!!!

// define what a single option's data looks like
export interface DropdownOptions {
    value: string,
    label: React.ReactNode,
    icon: React.ElementType;
    disabled?: boolean;
}

// The props for the Dropdown component
export interface DropdownProps {
    options: DropdownOptions[];
    placeholder: string;
    onSelect: (value: string) => void;
    value: string | null;
}


export const Dropdown = ({options, placeholder, onSelect, value}: DropdownProps) => {

    // State to manage the open/close status of the dropdown
    const [isOpen, setIsOpen] = useState(false);

    const [selectedOption, setSelectedOption] = useState<DropdownOptions | null>(null); // we say null because initially nothing is selected

    useEffect(() => {
        if (value === null) {
            setSelectedOption(null);
        } else {
            const option = options.find(opt => opt.value === value);
            setSelectedOption(option || null);
        }
    }, [value, options]);

    // function to handle option selection
    const handleSelect = (option: DropdownOptions) => {
        if (option.disabled) {
            return
        }
        onSelect(option.value);
        setIsOpen(false);
    }
    return (
        <div
            className="relative w-full"> {/* very important tailwind class as it ensures the dropdown menu is relative to its container  */}

            {/* The button that toggles the dropdown */}
            <button onClick={() => setIsOpen(!isOpen)}
                    className="w-full flex items-center justify-between bg-gray-100 text-gray-700 py-3 px-4 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 hover:ring-2 hover:ring-blue-500 cursor-default">
                <div className="flex items-center gap-3">
                    {selectedOption ? (
                        <>
                            <selectedOption.icon className="size-5"/>
                            <span className="text-gray-800">{selectedOption.label}</span>
                        </>
                    ) : (
                        <span className="text-gray-500">{placeholder}</span>
                    )}
                </div>

                {/* Chevron icon that rotates when dropdown is open */}
                <ChevronDownIcon className={`size-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}/>
            </button>

            {/* The dropdown menu and the invisible backdrop */}
            {isOpen && (
                <>
                    {/* The Invisible Backdrop */}
                    {/* This covers the whole screen and closes the dropdown when clicked. */}
                    <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsOpen(false)}
                    ></div>

                    {/* Your Dropdown Menu */}
                    <ul className={`absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden transition-all duration-170 ease-in-out origin-top motion-reduce:transition-none ${isOpen ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-95 pointer-events-none'}`}>

                        {/* Looping through the options to create list items */}
                        {options.map((option) => (
                            <li key={option.value}
                                onClick={() => handleSelect(option)}
                                className={`flex items-center justify-between p-3 cursor-default
                                ${option.disabled
                                    ? 'opacity-40 cursor-not-allowed'
                                    : 'hover:bg-gray-100'
                                }`}>
                                <div className="flex items-center gap-3">
                                    <option.icon className="size-5"/>
                                    <span>{option.label}</span>
                                </div>

                                {/* Check icon to indicate the selected option */}
                                {selectedOption?.value === option.value && <Check className="size-5 text-blue-500"/>}
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
};
